import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TStatusBadgeProps } from '@treezor/design-system';

import { IScaWallet, TScaStatus } from '@/store/models/sca.model';

interface IUseSCAActionsProps {
  scaWallet: IScaWallet;
}

type TStatusObject<K extends TScaStatus> = {
  [P in K]: TStatusBadgeProps;
};

export function useSCAStatus({ scaWallet }: IUseSCAActionsProps) {
  const { t } = useTranslation();
  const scaStatus = useMemo((): TScaStatus => {
    if (scaWallet.status === 'DELETED') {
      return 'DELETED';
    }
    if (scaWallet.locked) {
      return 'LOCKED';
    }
    if ((scaWallet.status === 'ACTIVE' || scaWallet.status === 'CREATED') && scaWallet.passcodeStatus === 'NOT_SET') {
      return 'PASSCODE_MISSING';
    }

    return 'ACTIVE';
  }, [scaWallet]);

  const statusMapping: TStatusObject<'LOCKED' | 'PASSCODE_MISSING' | 'DELETED' | 'ACTIVE'> = {
    LOCKED: { label: t('components.molecules.statusLabel.BLOCKED'), color: 'darkRed' },
    PASSCODE_MISSING: {
      label: t('components.molecules.statusLabel.PASSCODE_MISSING'),
      color: 'orange',
    },

    DELETED: {
      label: t('components.molecules.statusLabel.DELETED'),
      color: 'red',
    },
    ACTIVE: {
      label: t('components.molecules.statusLabel.ACTIVE'),
      color: 'green',
    },
  };

  return {
    scaStatus,
    scaStatusProps: statusMapping[scaStatus],
  };
}
